import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { m as motion } from "framer-motion";
import api, { resolveMedia } from "../lib/api";
import { statusLabel } from "../lib/labels";
import SafeImage from "../components/SafeImage";
import Seo from "../components/Seo";
import KineticLines from "../components/KineticLines";
import AmazonBuyButton from "../components/AmazonBuyButton";
import NovelRightsPanel from "../components/NovelRightsPanel";
import proFr from "../locales/fr/pro.json";

const RIGHTS_LABELS = {
  edition_fr: "Édition en langue française",
  etrangers: "Droits étrangers",
  traduction: "Traduction",
  audio: "Livre audio",
  bd: "Bande dessinée",
  cinema: "Cinéma",
  serie: "Série",
  documentaire: "Documentaire",
  adaptation: "Adaptation audiovisuelle",
};

export default function NovelDetail() {
  const { slug, tomePath } = useParams();
  // RR v7 doesn't support "tome-:tomeNum" (param inside segment). We match a
  // wildcard `tomePath` and parse the "tome-N" prefix here. If the URL used
  // /romans/{slug}/tome-1 → tomeNum = "1". Anything else → undefined.
  const tomeNum = tomePath && /^tome-(\d+)$/.test(tomePath) ? tomePath.replace("tome-", "") : undefined;
  const [novel, setNovel] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/novels/${slug}`).then((r) => setNovel(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="theme-ivory min-h-screen pt-40 px-6 lg:px-12">
        <div className="max-w-[900px] mx-auto text-center">
          <p className="eyebrow" style={{ color: "var(--copper)" }}>Erreur 404</p>
          <h1 className="font-display text-6xl mt-4 text-[var(--ink)]">Roman introuvable</h1>
          <Link to="/romans" className="btn-editorial filled mt-10 inline-flex">Retour au catalogue</Link>
        </div>
      </div>
    );
  }
  if (!novel) return <div className="theme-ivory min-h-screen" />;

  // Canonical URL: Malo trilogy uses /romans/{slug}/tome-{N} pattern.
  const canonicalPath =
    novel.trilogy_key === "malo" && novel.trilogy_tome
      ? `/romans/${novel.slug}/tome-${novel.trilogy_tome}`
      : `/romans/${novel.slug}`;

  return (
    <div>
      <Seo
        path={canonicalPath}
        title={`${novel.title}${novel.subtitle ? " — " + novel.subtitle : ""}`}
        description={novel.short_description || novel.back_cover?.slice(0, 200) || `Roman de Bruno Courtin, ${novel.genre || "roman français"}.`}
        image={novel.cover_url || novel.hero_image_url}
        type="book"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: `${novel.title}${novel.subtitle ? " — " + novel.subtitle : ""}`,
          author: { "@type": "Person", name: "Bruno Courtin", url: "https://brunocourtin.fr/auteur" },
          inLanguage: "fr-FR",
          genre: novel.genre,
          description: novel.short_description || novel.back_cover,
          url: `https://brunocourtin.fr${canonicalPath}`,
          image: novel.cover_url ? (novel.cover_url.startsWith("http") ? novel.cover_url : `https://brunocourtin.fr${novel.cover_url}`) : undefined,
          ...((novel.purchase_links || []).find((l) => l.store === "amazon" && l.url) && {
            sameAs: [novel.purchase_links.find((l) => l.store === "amazon" && l.url).url],
          }),
        }}
      />
      {/* Hero */}
      <section className="theme-ivory pt-40 pb-16 px-6 lg:px-12 relative overflow-hidden" data-testid="novel-hero">
        {novel.hero_image_url && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-25"
            style={{ backgroundImage: `url(${resolveMedia(novel.hero_image_url)})`, filter: "sepia(0.4) contrast(0.9)" }}
          />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(245,239,228,0.7) 0%, var(--ivory) 100%)" }} />
        <div className="relative max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8">
            <Link to="/romans" className="eyebrow inline-block mb-6" style={{ color: "var(--copper)" }} data-testid="back-to-catalog">← Catalogue</Link>
            <p className="eyebrow mb-4">{novel.genre}</p>
            <h1 className="font-display font-light text-6xl md:text-8xl lg:text-[9rem] leading-[0.88] text-[var(--ink)]">
              <KineticLines lines={[novel.title]} delay={0.1} />
            </h1>
            {novel.subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="font-display italic text-3xl md:text-5xl text-[var(--brown)] mt-4"
              >
                {novel.subtitle}
              </motion.p>
            )}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
              className="mt-10 flex flex-wrap gap-6 items-center"
            >
              {novel.period && <div><p className="eyebrow" style={{ color: "var(--copper)" }}>Période</p><p className="font-display text-xl mt-1 text-[var(--ink)]">{novel.period}</p></div>}
              <div><p className="eyebrow" style={{ color: "var(--copper)" }}>Statut</p><p className="font-display text-xl mt-1 text-[var(--ink)]">{statusLabel(novel.status)}</p></div>
            </motion.div>
          </div>
          <div className="col-span-12 md:col-span-4">
            <div className="book-frame max-w-[360px] mx-auto">
              {novel.cover_url ? (
                <SafeImage src={novel.cover_url} alt={novel.title} />
              ) : novel.hero_image_url ? (
                <SafeImage src={novel.hero_image_url} alt={novel.title} />
              ) : (
                <div className="cover-placeholder">
                  <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>Bruno Courtin</p>
                  <div>
                    <h3 className="font-display text-4xl leading-none">{novel.title}</h3>
                    {novel.subtitle && <p className="font-display italic text-xl mt-2">{novel.subtitle}</p>}
                  </div>
                  <div className="rule-copper" />
                </div>
              )}
            </div>
            <AmazonBuyButton novel={novel} className="mt-6 max-w-[360px] mx-auto" />
            {/* Public reading page: available for Malo T1, Malo T3, and NeuroAurum */}
            {((novel.slug === "malo-les-routes-secretes-de-locean-indien" && novel.trilogy_tome === 1) ||
              (novel.slug === "malo-la-route-du-soleil-levant" && novel.trilogy_tome === 3) ||
              novel.slug === "les-chaines-du-vivant-tome-1-neuroaurum") && (
              <Link
                to={
                  novel.slug === "malo-les-routes-secretes-de-locean-indien"
                    ? "/romans/malo-tome-1/premiers-chapitres"
                    : novel.slug === "malo-la-route-du-soleil-levant"
                    ? "/romans/malo-tome-3/premiers-chapitres"
                    : "/romans/les-chaines-du-vivant-tome-1-neuroaurum/premiers-chapitres"
                }
                className="btn-editorial mt-3 max-w-[360px] mx-auto flex items-center justify-center"
                data-testid="cta-read-sample"
              >
                Lire les premiers chapitres
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Résumé & 4e de couverture */}
      <section className="theme-parchment px-6 lg:px-12 py-24 md:py-32" data-testid="novel-summary">
        <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="eyebrow" style={{ color: "var(--copper)" }}>Quatrième de couverture</p>
          </div>
          <div className="col-span-12 md:col-span-8 md:pl-8">
            <p className="font-display text-2xl md:text-3xl leading-[1.4] text-[var(--brown)] max-w-3xl whitespace-pre-line">
              {novel.back_cover || novel.short_description}
            </p>
          </div>
        </div>
      </section>

      {/* Trilogie */}
      {novel.trilogy && novel.trilogy.length > 0 && (
        <section className="theme-ink px-6 lg:px-12 py-24 md:py-32" data-testid="novel-trilogy">
          <div className="max-w-[1440px] mx-auto">
            <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>La trilogie</p>
            <h2 className="font-display text-5xl md:text-7xl mt-3">Trois routes, une même quête</h2>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
              {novel.trilogy.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, delay: i * 0.1 }}
                  className="border border-[var(--gold)]/30 p-8"
                >
                  <p className="chapter-num" style={{ color: "var(--gold-soft)" }}>{t.tome}</p>
                  <h3 className="font-display text-2xl md:text-3xl mt-4 leading-tight">{t.title}</h3>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-widest opacity-70">{t.status}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Chronologie */}
      {novel.timeline && novel.timeline.length > 0 && (
        <section className="theme-ivory px-6 lg:px-12 py-24 md:py-32" data-testid="novel-timeline">
          <div className="max-w-[1440px] mx-auto">
            <p className="eyebrow" style={{ color: "var(--copper)" }}>Frise chronologique</p>
            <h2 className="font-display text-5xl md:text-6xl mt-3 text-[var(--ink)]">Le voyage</h2>
            <div className="mt-16">
              {novel.timeline.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="grid grid-cols-12 gap-6 py-8 border-t border-[var(--line)] items-baseline"
                >
                  <div className="col-span-12 md:col-span-3 font-mono text-[13px] uppercase tracking-widest text-[var(--copper)]">
                    {t.date}
                  </div>
                  <div className="col-span-12 md:col-span-9 font-display text-2xl md:text-3xl text-[var(--ink)] leading-snug">
                    {t.event}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Note d'intention */}
      {novel.intent_note && (
        <section className="theme-parchment px-6 lg:px-12 py-24" data-testid="novel-intent">
          <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <p className="eyebrow" style={{ color: "var(--copper)" }}>Note d'intention</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <p className="font-display italic text-2xl md:text-3xl leading-relaxed text-[var(--brown)] max-w-2xl">
                « {novel.intent_note} »
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Personnages */}
      {novel.characters && (
        <section className="theme-ivory px-6 lg:px-12 py-24" data-testid="novel-characters">
          <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <p className="eyebrow" style={{ color: "var(--copper)" }}>Personnages principaux</p>
            </div>
            <div className="col-span-12 md:col-span-8 font-body text-[17px] leading-[1.9] text-[var(--brown)]">
              {novel.characters}
            </div>
          </div>
        </section>
      )}

      {/* Droits */}
      {novel.rights_enabled && novel.rights?.length > 0 && (
        <section className="theme-ink px-6 lg:px-12 py-24 md:py-32" data-testid="novel-rights">
          <div className="max-w-[1440px] mx-auto">
            <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>Droits disponibles</p>
            <h2 className="font-display text-4xl md:text-5xl mt-3">Adaptations & cessions</h2>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {novel.rights.map((r) => (
                <div key={r} className="border border-[var(--gold)]/30 px-4 py-4 text-[12px] font-mono uppercase tracking-widest">
                  {RIGHTS_LABELS[r] || r}
                </div>
              ))}
            </div>
            <div className="mt-14 bg-[var(--parchment)]/8 rounded-md p-8">
              <h3 className="font-display text-2xl mb-6 text-[var(--parchment)]" data-testid="novel-fr-independent-heading">
                {novel.status === "publie" ? "Édition indépendante" : "Statut éditorial et droits"}
              </h3>
              <NovelRightsPanel novel={novel} locale="fr" t={proFr} />
            </div>
            <div className="mt-10">
              <Link to="/editeurs-agents" className="btn-editorial filled" data-testid="novel-cta-pro">
                Contacter pour les droits
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
